import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, ArrowRight } from 'lucide-react';
import { useLanguage } from '../../App';
import { t } from '../../lib/i18n';
import { recommendFromText } from '../../lib/articleRecommend';
import { getArticle, tipsPath } from '../../seo/articles';

/**
 * Under a coach message: up to two guide articles matching the levers the answer talks about.
 * Deterministic keyword scoring (see lib/articleRecommend.js), nothing is sent to the backend.
 */
export const ArticleSuggestions = ({ text }) => {
  const { language } = useLanguage();
  const articles = useMemo(() => recommendFromText(text).map(k => getArticle(k, language)).filter(Boolean), [text, language]);
  if (!articles.length) return null;
  return (
    <div className="mt-4 pt-3 border-t border-white/[0.08]" data-testid="chat-article-suggestions">
      <div className="flex items-center justify-between gap-3 mb-2">
        <p className="text-[11px] uppercase tracking-[0.18em] text-[var(--lux-gold)] inline-flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> {t('keepLearning', language)}</p>
        <Link to={tipsPath(language)} className="text-[11px] text-white/40 hover:text-[var(--lux-gold)] transition-colors duration-200" data-testid="chat-article-all-tips">{t('allTipsLink', language)}</Link>
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {articles.map(a => (
          <li key={a.key}>
            <Link to={a.path} className="group flex items-center gap-3 rounded-xl border border-white/10 bg-black/20 hover:border-lux-gold/40 transition-[border-color] duration-200 p-2 pr-3" data-testid={`chat-article-${a.key}`}>
              <img src={a.cover} alt="" width="96" height="64" loading="lazy" className="w-16 h-12 rounded-lg object-cover shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="block text-[10px] uppercase tracking-wider text-white/40">{a.axisName}</span>
                <span className="block text-sm text-white leading-snug line-clamp-2">{a.title}</span>
              </span>
              <ArrowRight className="w-4 h-4 text-[var(--lux-gold)] shrink-0 opacity-70 group-hover:opacity-100 transition-opacity duration-200" />
            </Link>
          </li>
        ))}
      </ul>
      <p className="mt-2 text-[11px] text-white/30">{t('keepLearningNote', language)}</p>
    </div>
  );
};
